/** Fixed option sets for the schedule wizard. The enum-backed ones must come
 *  from controls like these — the API parses them as enums, so a free-text
 *  value is a 400 rather than an ignored filter. */

import type {
  CadenceMode,
  IntervalUnitSeconds,
  OverlapPolicy,
} from '../types/catalog-ops-schedule.types';

/**
 * Eligibility is NOT decided in the frontend, and the job listing is NOT scoped
 * by origin — the picker lists every batch and lets the server judge.
 *
 * Two reasons no origin rule lives here. `/jobs` reports this field with a
 * different vocabulary than the schedule contract uses (`BatchSourceWire` is
 * `SCHEDULED | MANUAL | TRIAL | (string & {})`, and the batch table's filter
 * offers `SCHEDULED` / `SCHEDULED_UX_TRIGGER` / `TRIAL` / `FEED_AUDIT`), so any
 * mapping between the two is a guess. And the gate is a server-side flag the
 * frontend cannot read — `catalog-ops.schedule.allow-scheduled-enrichment-sources`
 * — so a hardcoded rule is stale the moment it flips.
 *
 * `POST /preview` decides instead: it runs on Continue from step one and returns
 * 422 for an origin the backend won't take, with no frontend change needed when
 * the flag changes. See `docs/catalog-operations-schedules.md`.
 *
 * The one exception is {@link EXCLUDED_SCHEDULE_JOB_SOURCES} below — a product
 * choice to hide two origins, NOT an eligibility guess. It is safe precisely
 * where the old origin rules were not: `TRIAL` and `FEED_AUDIT` are the same
 * token in `/jobs` (`BatchRecord.source`) and the schedule contract, so there is
 * no vocabulary to mismatch and no server flag involved.
 */

/** Batch origins kept out of the schedule picker. This is a UX filter, not the
 *  eligibility gate — the backend still judges every submitted job via
 *  `POST /preview` (422). A one-off `TRIAL` and a diagnostic `FEED_AUDIT` are
 *  simply not batches anyone puts on a repeating catalog-sync cadence, so
 *  listing them only clutters the picker. See `docs/catalog-operations-schedules.md`. */
export const EXCLUDED_SCHEDULE_JOB_SOURCES = new Set<string>(['TRIAL', 'FEED_AUDIT']);

/** Display labels for a batch's origin. Mirrors the batch table's own `source`
 *  filter so the same value reads the same way in both places. */
export const JOB_SOURCE_LABELS: Record<string, string> = {
  SCHEDULED: 'Scheduled job',
  SCHEDULED_UX_TRIGGER: 'Manual trigger',
  MANUAL: 'Manual',
  TRIAL: 'Trial workflow',
  FEED_AUDIT: 'Feed audit',
  CATALOG_OPERATIONS: 'Catalog operations',
  CATALOG_OPERATIONS_SCHEDULED: 'Scheduled catalog operations',
};

/** Cadence modes in the order the segmented control shows them — plain-language
 *  first, cron last for the operators who want it. */
export const CADENCE_MODES: readonly CadenceMode[] = [
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'INTERVAL',
  'CRON',
];

export const INTERVAL_UNITS: readonly IntervalUnitSeconds[] = [60, 3600, 86400];

/** Re-exported from the shared constant so catalog-ops and integrations offer
 *  the same quick-pick zones. */
export { SCHEDULE_TIME_ZONES } from '~/constants/schedule-time-zones';

/** The three overlap policies worth putting in front of everyone. */
export const PRIMARY_OVERLAP_POLICIES: readonly OverlapPolicy[] = [
  'SKIP',
  'BUFFER_ONE',
  'ALLOW_ALL',
];

/** Accepted by the API but rarely wanted for catalog work — `BUFFER_ALL` drains
 *  a backlog of full-catalog runs, and `CANCEL_OTHER` throws away an
 *  in-flight enrichment. Kept behind a disclosure. */
export const SECONDARY_OVERLAP_POLICIES: readonly OverlapPolicy[] = ['BUFFER_ALL', 'CANCEL_OTHER'];

/** Catch-up windows, in seconds. `600` is the value the reference requests use
 *  and the default here; `0` means a missed fire is dropped outright. */
export const CATCHUP_WINDOW_OPTIONS = [0, 300, 600, 3600, 21600] as const;

export const DEFAULT_CATCHUP_WINDOW_SECONDS = 600;

/** Page size for the schedules list. The API defaults to 20 and caps at 200;
 *  the section is collapsed by default, so one page is plenty. */
export const SCHEDULES_PAGE_SIZE = 20;

/** How many jobs the wizard's picker loads. The list is searchable, and a
 *  schedulable job is almost always a recent one. */
export const SCHEDULE_JOB_PICKER_SIZE = 100;
